import {
    BookOpen,
    UsersRound,
    Library,
    Milestone,
    Compass,
    DraftingCompass,
    Binoculars,
    Sprout,
    Brain,
    Highlighter
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { type ReactNode } from 'react';
import { useUrl } from 'crossroad';
import { IconTrowel } from '@tabler/icons-react';
import { Analytics } from '@/lib/analytics';
import { useGuidedStudyByPassageData } from '@/data/study-guide/guided-study-by-passage-data-context';
import { useStudyGuideByPassageData } from '@/data/study-guide/study-guide-by-passage-data-context';
import { useManuscript } from '@/context/manuscript-context';

interface ContextDropdownProps {
    passageId?: string;
    category: 'biblical' | 'ancient' | 'modern';
    children: ReactNode;
    onBeginGuidedStudy?: () => void;
}

interface ContextLink {
    id: string;
    title: string;
    icon: React.ComponentType<{ className?: string }>;
    description: string;
    category: ContextDropdownProps['category'];
    onClick: () => void;
}

const CATEGORY_TITLES = {
    biblical: { prefix: 'The World ', highlight: 'WITHIN', suffix: ' the Text' },
    ancient: { prefix: 'The World ', highlight: 'BEHIND', suffix: ' the Text' },
    modern: { prefix: 'The World ', highlight: 'WITH', suffix: ' the Text' }
} as const;

export default function ContextDropdown({ category, children, passageId, onBeginGuidedStudy }: ContextDropdownProps) {
    const [, setUrl] = useUrl();
    const { data: guidedStudy, isLoading: isGuidedStudyLoading } = useGuidedStudyByPassageData(passageId);
    const { data: studyGuide, isLoading: isStudyGuideLoading } = useStudyGuideByPassageData(passageId);
    const { setManuallyOpened } = useManuscript();

    const hasGuidedStudy = !!guidedStudy;
    const hasStudyGuide = !!studyGuide;

    // Don't show the dropdown if we're not on a passage details page
    if (!passageId) {
        return null;
    }

    const contextLinks: ContextLink[] = [
        // Biblical Context
        {
            id: 'book-context',
            title: 'Book Context',
            icon: BookOpen,
            description: 'Where does this fit in the book?',
            category: 'biblical' as ContextLink['category'],
            onClick: () => {
                Analytics.contextPanelOpen('literary');
                setUrl(`/app/passage/${passageId}/context/book`);
            }
        },
        {
            id: 'scriptural-context',
            title: 'Scriptural Context',
            icon: Library,
            description: 'How does this echo Scripture?',
            category: 'biblical' as ContextLink['category'],
            onClick: () => {
                Analytics.contextPanelOpen('literary');
                setUrl(`/app/passage/${passageId}/context/scripture`);
            }
        },
        {
            id: 'literary-context',
            title: 'Literary Context',
            icon: DraftingCompass,
            description: 'What\'s the literary design?',
            category: 'biblical' as ContextLink['category'],
            onClick: () => {
                Analytics.contextPanelOpen('literary');
                setUrl(`/app/passage/${passageId}/context/literary`);
            }
        },
        // {
        // note: this one doesn't work, and would need to go into draw mode. I don't think we need this.
        //     id: 'marking-tools',
        //     title: 'Manuscript Discovery',
        //     icon: Highlighter,
        //     description: 'What can you find in the text?',
        //     category: 'biblical' as ContextLink['category'],
        //     onClick: () => {
        //         setUrl(`/app/passage/${passageId}`);
        //         setManuallyOpened(true);
        //     }
        // },
        // {
        //     id: 'canon',
        //     title: '[OLD] Outline',
        //     icon: BookOpen,
        //     description: 'View where this passage sits within the broader biblical canon',
        //     category: 'biblical' as ContextLink['category'],
        //     onClick: () => setUrl(`/app/passage/${passageId}/context/canon`)
        // },
        // Ancient World Context
        {
            id: 'historical-events',
            title: 'Historical Context',
            icon: Milestone,
            description: 'When did this happen?',
            category: 'ancient' as ContextLink['category'],
            onClick: () => {
                Analytics.contextPanelOpen('historical');
                setUrl(`/app/passage/${passageId}/context/historicalEvents`);
            }
        },
        {
            id: 'places',
            title: 'Geographical Context',
            icon: Compass,
            description: 'Where did this happen?',
            category: 'ancient' as ContextLink['category'],
            onClick: () => {
                Analytics.contextPanelOpen('people');
                setUrl(`/app/passage/${passageId}/context/places`);
            }
        },
        {
            id: 'archaeology',
            title: 'Archaeological Context',
            icon: IconTrowel,
            description: 'What\'s been uncovered?',
            category: 'ancient' as ContextLink['category'],
            onClick: () => {
                Analytics.contextPanelOpen('archaeological');
                setUrl(`/app/passage/${passageId}/context/archaeology`);
            }
        },
        {
            id: 'cultural',
            title: 'Cultural Context',
            icon: UsersRound,
            description: 'What was their world like?',
            category: 'ancient' as ContextLink['category'],
            onClick: () => {
                Analytics.contextPanelOpen('cultural');
                setUrl(`/app/passage/${passageId}/context/culture`);
            }
        },
        // Modern Context
        {
            id: 'begin-guided-study',
            title: 'Guided Study',
            icon: Binoculars,
            description: 'Ready for a guided journey?',
            category: 'modern' as ContextLink['category'],
            onClick: () => {
                if (onBeginGuidedStudy) {
                    onBeginGuidedStudy();
                }
            }
        },
        {
            id: 'inductive-study',
            title: 'Inductive Study',
            icon: Brain,
            description: 'What questions should I ask?',
            category: 'modern' as ContextLink['category'],
            onClick: () => {
                Analytics.inductiveView(passageId);
                setUrl(`/app/passage/${passageId}/context/inductive`);
            }
        },
        {
            id: 'engagement-methods',
            title: 'Ideas for Engagement',
            icon: Sprout,
            description: 'How can I engage with this?',
            category: 'modern' as ContextLink['category'],
            onClick: () => {
                Analytics.contextPanelOpen('literary');
                setUrl(`/app/passage/${passageId}/context/engagementMethods`);
            }
        },
        {
            id: 'leader-toolkit',
            title: "Leader's Toolkit",
            icon: UsersRound,
            description: 'How can I help others engage?',
            category: 'modern' as ContextLink['category'],
            onClick: () => {
                Analytics.contextPanelOpen('leaderToolkit' as any);
                setUrl(`/app/passage/${passageId}/context/leaderToolkit`);
            }
        }
    ].filter(link => {
        if (link.category !== category) return false;
        // Guided Study is now always shown (will show skeleton/empty state in subheader)
        if (link.id === 'inductive-study' && !hasStudyGuide) return false;
        if (link.id === 'leader-toolkit' && !hasStudyGuide) return false; // Toolkit depends on study guide generation
        return true;
    });

    const groupedLinks = contextLinks.reduce((acc, link) => {
        if (!acc[link.category]) {
            acc[link.category] = [];
        }
        acc[link.category].push(link);
        return acc;
    }, {} as Record<string, ContextLink[]>);

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    {children}
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" className="w-64">
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground/60 py-2.5 px-3">
                        {CATEGORY_TITLES[category].prefix}
                        <span className="text-[#d4af37]">{CATEGORY_TITLES[category].highlight}</span>
                        {CATEGORY_TITLES[category].suffix}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="opacity-50" />

                    {Object.entries(groupedLinks).map(([category, links], categoryIndex) => (
                        <div key={category}>
                            <DropdownMenuGroup>
                                {links.map((link) => (
                                    <DropdownMenuItem
                                        key={link.id}
                                        onClick={link.onClick}
                                        className="flex items-start gap-3 p-3 cursor-pointer hover:bg-muted/50 focus:bg-muted/50 hover:text-foreground focus:text-foreground"
                                    >
                                        <link.icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-sm">{link.title}</div>
                                            <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                                                {link.description}
                                            </div>
                                        </div>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuGroup>
                            {categoryIndex < Object.entries(groupedLinks).length - 1 && (
                                <DropdownMenuSeparator />
                            )}
                        </div>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );
}
